import React from 'react';
import { Typography, Grid, Card, CardContent } from '@mui/material';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ScatterChart, Scatter
} from 'recharts';
import { ChartDataPoint } from '../types';

interface PowerRankingChartsProps {
  chartData: ChartDataPoint[];
}

// Custom tooltip component with comprehensive team info
const CustomTooltip = ({ active, payload, label, dataKey }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="custom-tooltip" style={{
        backgroundColor: '#1565c0',
        padding: '10px',
        borderRadius: '6px',
        color: 'white',
        boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
        minWidth: '180px',
        fontSize: '12px'
      }}>
        <p style={{ fontWeight: 'bold', marginBottom: '6px', fontSize: '13px' }}>
          {data.fullName}
        </p>
        <p style={{ margin: '2px 0', fontSize: '11px' }}>
          <strong>Record:</strong> {data.wins}-{data.losses}{data.ties ? `-${data.ties}` : ''}
        </p>
        <p style={{ margin: '2px 0', fontSize: '11px' }}>
          <strong>Win %:</strong> {data.winPct}%
        </p>
        <p style={{ margin: '2px 0', fontSize: '11px' }}>
          <strong>Power Rank:</strong> #{data.powerRank} of {data.powerRank + 10}
        </p>
        <p style={{ margin: '2px 0', fontSize: '11px' }}>
          <strong>Points Rank:</strong> #{data.pointsRank} of {data.pointsRank + 10}
        </p>
        <p style={{ margin: '2px 0', fontSize: '11px' }}>
          <strong>Total Points:</strong> {data.totalPoints}
        </p>
        {dataKey === 'coachingEfficiency' && (
          <>
            <p style={{ margin: '2px 0', fontSize: '11px' }}>
              <strong>Coaching Rank:</strong> #{data.coachRank} of {data.coachRank + 10}
            </p>
            <p style={{ margin: '2px 0', fontSize: '11px' }}>
              <strong>Points Left on Bench:</strong> {data.pointsLeft}
            </p>
          </>
        )}
        {(dataKey === 'optimalPoints' || dataKey === 'actualPoints') && (
          <>
            <p style={{ margin: '2px 0', fontSize: '11px' }}>
              <strong>Optimal Points:</strong> {data.optimalPoints}
            </p>
            <p style={{ margin: '2px 0', fontSize: '11px' }}>
              <strong>Actual Points:</strong> {data.actualPoints}
            </p>
            <p style={{ margin: '2px 0', fontSize: '11px' }}>
              <strong>Coaching Efficiency:</strong> {data.coachingEfficiency}%
            </p>
          </>
        )}
      </div>
    );
  }
  return null;
};

// Custom X-axis component that renders team icons instead of text
const TeamIconXAxis = ({ x, y, payload, chartData }: any) => {
  if (!payload || !payload.value) return null;
  
  // Find the team data based on the team name
  const teamName = payload.value;
  const teamDataItem = chartData?.find((item: any) => item.name === teamName || item.fullName === teamName);
  const iconUrl = teamDataItem?.teamData?.picture;
  
  if (!iconUrl) {
    return (
      <g>
        <text x={x} y={y + 4} fill="#666" textAnchor="middle" fontSize="10">
          {teamDataItem?.teamData?.shortName || teamName || 'Team'}
        </text>
      </g>
    );
  }

  return (
    <g>
      <foreignObject x={x - 10} y={y - 8} width="20" height="20">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', height: '100%' }}>
          <img 
            src={iconUrl} 
            alt={teamDataItem?.fullName || teamName}
            style={{ 
              width: '18px', 
              height: '18px', 
              borderRadius: '50%',
              objectFit: 'cover'
            }}
            onError={(e) => {
              // Fallback to text if image fails to load
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `<div style="font-size: 8px; color: #666; text-align: center; line-height: 20px;">${teamDataItem?.teamData?.shortName || teamName || 'Team'}</div>`;
              }
            }}
          />
        </div>
      </foreignObject>
    </g>
  );
};

export function PowerRankingCharts({ chartData }: PowerRankingChartsProps) {
  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Win % vs Total Points</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <ScatterChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="totalPoints" name="Points" tick={{ fontSize: 10 }} />
                <YAxis dataKey="winPct" name="Win %" tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip dataKey="scatter" />} />
                <Scatter dataKey="winPct" fill="#82ca9d" />
              </ScatterChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Coaching Efficiency</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[...chartData].sort((a, b) => b.coachingEfficiency - a.coachingEfficiency)} margin={{ bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={<TeamIconXAxis chartData={chartData} />}
                  height={40}
                  interval={0}
                />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip dataKey="coachingEfficiency" />} />
                <Bar dataKey="coachingEfficiency" fill="#ffc658" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Optimal vs Actual Points</Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={[...chartData].sort((a, b) => a.powerRank - b.powerRank)} margin={{ bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={<TeamIconXAxis chartData={chartData} />}
                  height={40}
                  interval={0}
                />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip content={<CustomTooltip dataKey="optimalPoints" />} />
                <Bar dataKey="optimalPoints" fill="#8884d8" name="Optimal Points" />
                <Bar dataKey="actualPoints" fill="#82ca9d" name="Actual Points" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}
